/* ===========================================================
   Lucraa — Landing · primitivas compartilhadas da página
   (section head, comparação antes→depois, split e triângulo ROAS;
   CSS em lucraa.css)
   =========================================================== */
import type { ReactNode } from "react";
import { Icon, KpiCard } from "../../components/ui.jsx";
import { fmt } from "../../domain/format";

/** Cabeçalho de seção: eyebrow + h2 + lead — escala única (.lp-section-head). */
export function LpSectionHead({
  eyebrow,
  title,
  lead,
  center = false,
  titleId,
  titleMaxWidth,
}: {
  eyebrow: string;
  title: ReactNode;
  lead?: ReactNode;
  center?: boolean;
  /** id do h2 — referenciado pelo aria-labelledby da <section>. */
  titleId?: string;
  /** exceção pontual à largura padrão de 720px (ex.: 760 na seção ROAS). */
  titleMaxWidth?: number;
}) {
  return (
    <div className={"lp-section-head lp-reveal" + (center ? " lp-section-head--center" : "")}>
      <span className="label-eyebrow">{eyebrow}</span>
      <h2 id={titleId} style={titleMaxWidth ? { maxWidth: titleMaxWidth } : undefined}>
        {title}
      </h2>
      {lead && <p className="lp-lead">{lead}</p>}
    </div>
  );
}

/** Comparação "antes → depois" — círculo-seta central; no mobile o glifo rotaciona 90°. */
export function LpVs({ antes, depois }: { antes: ReactNode; depois: ReactNode }) {
  return (
    <div className="lp-vs">
      <div className="card lp-card lp-reveal">{antes}</div>
      <div className="lp-vs-arrow" aria-hidden="true">
        <Icon name="arrowRight" size={24} color="#fff" />
      </div>
      <div className="card lp-card lp-vs-depois lp-reveal">{depois}</div>
    </div>
  );
}

/** Split de feature: coluna de texto ↔ coluna de mockup (do módulo "Meu Lucro"). */
export function LpSplit({
  texto,
  mock,
  reverse = false,
}: {
  texto: ReactNode;
  mock: ReactNode;
  reverse?: boolean;
}) {
  return (
    <div className={"lp-split" + (reverse ? " lp-split--rev" : "")}>
      <div className="lp-split-text">{texto}</div>
      <div className="lp-split-mock">{mock}</div>
    </div>
  );
}

/** Triângulo do ROAS: informado × real × equilíbrio (3 KpiCards reais do app).
 *  `equilibrio` é number|null — null vira "—" + ajuda, nunca zero (dinheiro). */
export function LpRoasTriangulo({
  informado,
  real,
  equilibrio,
}: {
  informado: number;
  real: number;
  equilibrio: number | null;
}) {
  return (
    <div className="lp-roas-tri lp-stagger">
      <KpiCard
        label="ROAS informado"
        valor={fmt.roas(informado)}
        delta={null}
        deltaWarn={false}
        foot="O número que a plataforma mostra — receita atribuída ÷ gasto."
        help="É a receita que a Meta/Google atribuem ao anúncio, sem descontar nenhum custo. Otimista por desenho."
        helpTitle="ROAS informado"
      />
      <KpiCard
        hero
        tom="lucro"
        label="ROAS real"
        valor={fmt.roas(real)}
        delta={null}
        deltaWarn={false}
        foot="Cada venda contada uma vez, pelo último clique — e com os custos descontados."
        help="O Lucraa conta cada pedido pago uma vez e desconta CMV, frete e imposto. Mais conservador, e o que de fato sobra."
        helpTitle="ROAS real"
      />
      <KpiCard
        label="ROAS de equilíbrio"
        valor={equilibrio == null ? "—" : fmt.roas(equilibrio)}
        delta={null}
        deltaWarn={false}
        foot={
          equilibrio == null
            ? "Precisa de custo cadastrado por SKU para calcular."
            : "Abaixo dele, o anúncio queima margem mesmo vendendo."
        }
        help="O ROAS mínimo para a campanha parar de consumir caixa. Sai da sua margem de contribuição."
        helpTitle="ROAS de equilíbrio"
      />
    </div>
  );
}
